import { useEffect, useState, useCallback } from "react";
import { fetchFinancial, createFinancial, updateFinancial, deleteFinancial, fmtMoney, fmtDateOnly } from "@/lib/api";
import { PageHeader, EmptyState, StatusChip, StatCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, Check, ArrowDown, ArrowUp, Wallet, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Legend, Tooltip,
} from "recharts";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { tipo: "receber", descricao: "", valor: 0, categoria: "Geral", data_vencimento: today(), status: "pendente" };

export default function Financial() {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("todos");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const reload = useCallback(() => fetchFinancial().then(setList), []);
  useEffect(() => { reload(); }, [reload]);

  const filtered = list.filter((t) => tab === "todos" ? true : t.tipo === tab);

  const totalReceberPend = list.filter((t) => t.tipo === "receber" && t.status === "pendente").reduce((s, t) => s + t.valor, 0);
  const totalPagarPend = list.filter((t) => t.tipo === "pagar" && t.status === "pendente").reduce((s, t) => s + t.valor, 0);
  const recebido = list.filter((t) => t.tipo === "receber" && t.status === "pago").reduce((s, t) => s + t.valor, 0);
  const pago = list.filter((t) => t.tipo === "pagar" && t.status === "pago").reduce((s, t) => s + t.valor, 0);
  const saldo = recebido - pago;

  const pieData = [
    { name: "A Receber", value: totalReceberPend, color: "#00B0F0" },
    { name: "A Pagar", value: totalPagarPend, color: "#EF4444" },
    { name: "Recebido", value: recebido, color: "#10B981" },
    { name: "Pago", value: pago, color: "#475569" },
  ].filter((d) => d.value > 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.descricao || !form.valor) { toast.error("Descrição e valor são obrigatórios"); return; }
    const payload = { ...form, valor: parseFloat(form.valor || 0) };
    if (payload.status === "pago" && !payload.data_pagamento) payload.data_pagamento = today();
    try {
      if (editId) await updateFinancial(editId, payload);
      else await createFinancial(payload);
      toast.success("Lançamento salvo");
      setOpen(false); setForm(empty); setEditId(null);
      reload();
    } catch { toast.error("Erro ao salvar"); }
  };

  const onEdit = (t) => { setEditId(t.id); setForm({ ...t }); setOpen(true); };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    await deleteFinancial(id);
    toast.success("Lançamento excluído");
    reload();
  };

  const onMarkPaid = async (t) => {
    await updateFinancial(t.id, { status: "pago", data_pagamento: today() });
    toast.success("Marcado como pago");
    reload();
  };

  return (
    <div data-testid="financial-page">
      <PageHeader
        title="Controle Financeiro"
        subtitle="Acompanhe contas a pagar, contas a receber e o saldo geral."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-tx-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">
                <Plus size={16} weight="bold" className="mr-1.5" /> Novo Lançamento
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[520px]">
              <DialogHeader>
                <DialogTitle className="font-heading">{editId ? "Editar Lançamento" : "Novo Lançamento"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo *</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger data-testid="tx-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="receber">A Receber</SelectItem>
                        <SelectItem value="pagar">A Pagar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="tx-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendente">Pendente</SelectItem>
                        <SelectItem value="pago">Pago</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Descrição *</Label>
                    <Input data-testid="tx-desc" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
                  </div>
                  <div>
                    <Label>Valor (R$) *</Label>
                    <Input data-testid="tx-valor" type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
                  </div>
                  <div>
                    <Label>Categoria</Label>
                    <Input data-testid="tx-cat" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} />
                  </div>
                  <div>
                    <Label>Data de Vencimento *</Label>
                    <Input data-testid="tx-venc" type="date" value={form.data_vencimento} onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} />
                  </div>
                  {form.status === "pago" && (
                    <div>
                      <Label>Data de Pagamento</Label>
                      <Input data-testid="tx-pag" type="date" value={form.data_pagamento || today()} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-tx-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">Salvar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="A Receber" value={fmtMoney(totalReceberPend)} icon={ArrowUp} accent />
        <StatCard label="A Pagar" value={fmtMoney(totalPagarPend)} icon={ArrowDown} />
        <StatCard label="Recebido" value={fmtMoney(recebido)} icon={Wallet} />
        <StatCard label="Saldo" value={fmtMoney(saldo)} icon={Scales} accent />
      </div>

      {/* Chart + table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-md p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#475569] mb-1">Distribuição</div>
          <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-4">Por Status</h3>
          <div className="h-[260px]">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50} paddingAngle={3}>
                    {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-[#475569]">Sem dados</div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-md">
          <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="todos" data-testid="tab-todos">Todos</TabsTrigger>
                <TabsTrigger value="receber" data-testid="tab-receber">A Receber</TabsTrigger>
                <TabsTrigger value="pagar" data-testid="tab-pagar">A Pagar</TabsTrigger>
              </TabsList>
            </Tabs>
            <span className="text-xs text-[#475569]">{filtered.length} lançamento(s)</span>
          </div>

          {filtered.length === 0 ? (
            <EmptyState title="Nenhum lançamento" message="Crie um novo lançamento financeiro para começar." />
          ) : (
            <Table>
              <TableHeader className="bg-[#F8FAFC]">
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t, i) => (
                  <TableRow key={t.id} className={i % 2 ? "bg-[#F8FAFC]" : ""} data-testid={`tx-row-${t.id}`}>
                    <TableCell>
                      <div className="font-medium">{t.descricao}</div>
                      <div className="text-xs text-[#475569]">{t.categoria}</div>
                    </TableCell>
                    <TableCell>
                      <span className={t.tipo === "receber" ? "text-[#10B981] font-medium" : "text-red-500 font-medium"}>
                        {t.tipo === "receber" ? "Receber" : "Pagar"}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#475569]">{fmtDateOnly(t.data_vencimento)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtMoney(t.valor)}</TableCell>
                    <TableCell><StatusChip status={t.status} /></TableCell>
                    <TableCell className="text-right">
                      {t.status === "pendente" && (
                        <Button variant="ghost" size="icon" onClick={() => onMarkPaid(t)} data-testid={`pay-tx-${t.id}`} title="Marcar como pago">
                          <Check size={16} className="text-green-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => onEdit(t)}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(t.id)}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
